import React from "react";
import { Box } from "@mui/material";
import { DotLegalAlert, DotLegalHeader, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { RiskAreaFrameChangeImpact } from "../RiskAreaDialog.types";
import { MappingRow, useFrameChangeFigures } from "./FrameChangeConfirmationStep.hooks";
import { useFrameChangeConfirmationStyles } from "./FrameChangeConfirmationStep.styles";

export interface IFrameChangeConfirmationStep {
    impact: RiskAreaFrameChangeImpact | undefined;
    // A scale change is the only kind that leaves anything to settle here: the criteria were authored on the form,
    // but the scores already given need a rule for how they are carried onto the new scale.
    isReBasing: boolean;
    fromScale: number;
    toScale: number;
    consequenceRows: Array<MappingRow>;
    probabilityRows: Array<MappingRow>;
    consequenceValid: boolean;
    probabilityValid: boolean;
    onConsequenceTargetChange: (sourceIndex: number, targetLevel: number) => void;
    onProbabilityTargetChange: (sourceIndex: number, targetLevel: number) => void;
    collapseWarnings: Array<string>;
}

export function FrameChangeConfirmationStep(props: IFrameChangeConfirmationStep) {
    const { translateString } = useTranslation();
    const styles = useFrameChangeConfirmationStyles();
    const { figures, hasImpact } = useFrameChangeFigures(props.impact);

    const renderMappingTable = (title: string, rows: Array<MappingRow>, isValid: boolean, onChange: (sourceIndex: number, targetLevel: number) => void) => (
        <Box>
            <DotLegalHeader headerStyle="extraSmall" color="primary" marginBottom={1}>
                {title}
            </DotLegalHeader>
            {rows.map((row, index) => (
                <Box key={row.sourceLevel} sx={styles.mappingRow}>
                    <Box sx={styles.sourceLabel}>{`${row.sourceLevel} ${row.sourceLabel}`}</Box>
                    <Box sx={styles.arrow}>→</Box>
                    <DotLegalTextField
                        label=""
                        debounce={false}
                        size="small"
                        onlyNumber="integer"
                        minNumberValue={1}
                        maxNumberValue={props.toScale}
                        value={row.targetLevel.toString()}
                        onChange={(value) => onChange(index, value === "" ? 0 : Number(value))}
                    />
                </Box>
            ))}
            {!isValid && <Box sx={styles.figureLabel}>{translateString("riskAreaReBaseMappingInvalid")}</Box>}
        </Box>
    );

    return (
        <Box>
            <DotLegalAlert
                severity="warning"
                warningText={translateString(props.isReBasing ? "riskAreaReBaseIntro" : "riskAreaFrameChangeWarning")}
                marginBottom={2}
            />

            {props.isReBasing && (
                <>
                    <Box sx={styles.mappingColumns}>
                        {renderMappingTable(
                            translateString("riskAreaConsequenceLevels"),
                            props.consequenceRows,
                            props.consequenceValid,
                            props.onConsequenceTargetChange
                        )}
                        {renderMappingTable(
                            translateString("riskAreaProbabilityLevels"),
                            props.probabilityRows,
                            props.probabilityValid,
                            props.onProbabilityTargetChange
                        )}
                    </Box>

                    {props.collapseWarnings.length > 0 && (
                        <DotLegalAlert
                            severity="info"
                            marginBottom={2}
                            warningHeader={translateString("riskAreaReBaseCollapseHeader")}
                            warningText={
                                <Box>
                                    {props.collapseWarnings.map((warning) => (
                                        <Box key={warning}>{warning}</Box>
                                    ))}
                                </Box>
                            }
                        />
                    )}
                </>
            )}

            {hasImpact ? (
                <Box sx={styles.figures}>
                    {figures.map((figure) => (
                        <Box key={figure.label} sx={styles.figureRow}>
                            <Box component={"span"} sx={styles.figureLabel}>
                                {figure.label}:
                            </Box>
                            <Box component={"span"} sx={styles.figureValue}>
                                {figure.value}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={styles.figureLabel}>{translateString("riskAreaFrameChangeNoImpact")}</Box>
            )}
        </Box>
    );
}
